/**
 * Pure squad-draft model for the Squad Builder. UI-framework-free so the
 * validation logic is unit-testable; the server RPC re-validates everything -
 * this exists to give instant, friendly feedback, not to be trusted.
 */
import type { FormationRules, Position } from '@scoring/rules.ts';
import type { PlayerWithTeam, SquadPickInput } from '@/lib/data/provider';

export interface DraftPick {
  player: PlayerWithTeam;
  isStarting: boolean;
  isCaptain: boolean;
  isVice: boolean;
}

export interface SquadDraft {
  picks: DraftPick[];
}

export const POSITIONS: Position[] = ['GK', 'DEF', 'MID', 'FWD'];

/** Map a fantasy price (~3.5..15.0m) onto a familiar 55..99 rating scale. */
export function priceToOvr(price: number): number {
  return Math.max(55, Math.min(99, Math.round(50 + price * 3.4)));
}

export function emptyDraft(): SquadDraft {
  return { picks: [] };
}

function shuffle<T>(items: T[]): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Build a RANDOM valid squad within the budget + formation. Picks the required
 * players per position at random, then swaps the priciest down for cheaper
 * same-position players until it fits, auto-fills the starting XI, and makes the
 * two priciest starters captain + vice. Different every time (pure luck);
 * returns null only if the pool genuinely can't field a squad.
 */
export function buildRandomDraft(
  pool: PlayerWithTeam[],
  rules: FormationRules,
  budget: number,
): SquadDraft | null {
  const byPos: Record<Position, PlayerWithTeam[]> = { GK: [], DEF: [], MID: [], FWD: [] };
  for (const player of pool) {
    if (byPos[player.position]) byPos[player.position].push(player);
  }
  for (const position of POSITIONS) {
    if (byPos[position].length < rules.squad[position]) return null;
  }

  for (let attempt = 0; attempt < 60; attempt += 1) {
    const shuffled: Record<Position, PlayerWithTeam[]> = {
      GK: shuffle(byPos.GK),
      DEF: shuffle(byPos.DEF),
      MID: shuffle(byPos.MID),
      FWD: shuffle(byPos.FWD),
    };
    const picks: PlayerWithTeam[] = [];
    for (const position of POSITIONS) picks.push(...shuffled[position].slice(0, rules.squad[position]));

    let cost = picks.reduce((sum, player) => sum + player.fantasy_price, 0);
    let guard = 0;
    while (cost > budget && guard < 400) {
      guard += 1;
      let idx = 0;
      for (let i = 1; i < picks.length; i += 1) {
        if (picks[i].fantasy_price > picks[idx].fantasy_price) idx = i;
      }
      const position = picks[idx].position;
      const taken = new Set(picks.map((player) => player.id));
      const cheaper = shuffled[position].filter(
        (player) => !taken.has(player.id) && player.fantasy_price < picks[idx].fantasy_price,
      );
      if (cheaper.length === 0) break;
      const replacement = cheaper[Math.floor(Math.random() * cheaper.length)];
      cost += replacement.fantasy_price - picks[idx].fantasy_price;
      picks[idx] = replacement;
    }
    if (cost > budget) continue;

    let draft = emptyDraft();
    for (const player of picks) {
      const result = addPlayer(draft, player, rules);
      if (result.ok) draft = result.draft;
    }
    if (draft.picks.length !== picks.length) continue;

    const starters = draft.picks
      .filter((pick) => pick.isStarting)
      .sort((a, b) => b.player.fantasy_price - a.player.fantasy_price);
    if (starters[0]) draft = setCaptain(draft, starters[0].player.id);
    if (starters[1]) draft = setVice(draft, starters[1].player.id);
    return draft;
  }
  return null;
}

export function draftCost(draft: SquadDraft): number {
  return Math.round(draft.picks.reduce((sum, pick) => sum + pick.player.fantasy_price, 0) * 10) / 10;
}

export function countByPosition(draft: SquadDraft, startersOnly = false): Record<Position, number> {
  const counts: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const pick of draft.picks) {
    if (startersOnly && !pick.isStarting) continue;
    counts[pick.player.position] += 1;
  }
  return counts;
}

export type DraftIssue =
  | { type: 'needs'; position: Position; count: number }
  | { type: 'overBudget'; amount: number }
  | { type: 'needCaptain' }
  | { type: 'needVice' }
  | { type: 'invalidFormation' };

/** All blocking issues, in display order. Empty array = saveable. */
export function validateDraft(
  draft: SquadDraft,
  rules: FormationRules,
  budget: number,
): DraftIssue[] {
  const issues: DraftIssue[] = [];
  const counts = countByPosition(draft);

  for (const position of POSITIONS) {
    const missing = rules.squad[position] - counts[position];
    if (missing > 0) {
      issues.push({ type: 'needs', position, count: missing });
    }
  }

  const cost = draftCost(draft);
  if (cost > budget) {
    issues.push({ type: 'overBudget', amount: Math.round((cost - budget) * 10) / 10 });
  }

  if (issues.length === 0) {
    const starters = countByPosition(draft, true);
    const totalStarters = POSITIONS.reduce((sum, position) => sum + starters[position], 0);
    const formationOk =
      totalStarters === rules.starting.size &&
      POSITIONS.every(
        (position) =>
          starters[position] >= rules.starting[position][0] &&
          starters[position] <= rules.starting[position][1],
      );
    if (!formationOk) {
      issues.push({ type: 'invalidFormation' });
    }
    if (!draft.picks.some((pick) => pick.isCaptain && pick.isStarting)) {
      issues.push({ type: 'needCaptain' });
    }
    if (!draft.picks.some((pick) => pick.isVice && pick.isStarting)) {
      issues.push({ type: 'needVice' });
    }
  }

  return issues;
}

export type AddResult =
  | { ok: true; draft: SquadDraft }
  | { ok: false; reason: 'positionFull' | 'alreadyPicked' };

/**
 * Add a player. Starts them automatically when the starting XI can still take
 * their position (max not reached and XI not full), otherwise benches them.
 */
export function addPlayer(draft: SquadDraft, player: PlayerWithTeam, rules: FormationRules): AddResult {
  if (draft.picks.some((pick) => pick.player.id === player.id)) {
    return { ok: false, reason: 'alreadyPicked' };
  }
  const counts = countByPosition(draft);
  if (counts[player.position] >= rules.squad[player.position]) {
    return { ok: false, reason: 'positionFull' };
  }
  const starters = countByPosition(draft, true);
  const totalStarters = POSITIONS.reduce((sum, position) => sum + starters[position], 0);
  const canStart =
    totalStarters < rules.starting.size &&
    starters[player.position] < rules.starting[player.position][1];

  return {
    ok: true,
    draft: {
      picks: [
        ...draft.picks,
        { player, isStarting: canStart, isCaptain: false, isVice: false },
      ],
    },
  };
}

export function removePlayer(draft: SquadDraft, playerId: string): SquadDraft {
  return { picks: draft.picks.filter((pick) => pick.player.id !== playerId) };
}

/** Swap a starter and a bench player of the same position (pitch drag/drop). */
export function swapPlayers(draft: SquadDraft, playerIdA: string, playerIdB: string): SquadDraft {
  const pickA = draft.picks.find((pick) => pick.player.id === playerIdA);
  const pickB = draft.picks.find((pick) => pick.player.id === playerIdB);
  if (!pickA || !pickB) return draft;
  if (pickA.isStarting === pickB.isStarting) return draft;
  if (pickA.player.position !== pickB.player.position) return draft;

  return {
    picks: draft.picks.map((pick) => {
      if (pick.player.id === playerIdA || pick.player.id === playerIdB) {
        const nowStarting = !pick.isStarting;
        return {
          ...pick,
          isStarting: nowStarting,
          // Captaincy stays with starters only.
          isCaptain: nowStarting ? pick.isCaptain : false,
          isVice: nowStarting ? pick.isVice : false,
        };
      }
      return pick;
    }),
  };
}

export function setCaptain(draft: SquadDraft, playerId: string): SquadDraft {
  const target = draft.picks.find((pick) => pick.player.id === playerId);
  if (!target || !target.isStarting) return draft;
  return {
    picks: draft.picks.map((pick) => ({
      ...pick,
      isCaptain: pick.player.id === playerId,
      isVice: pick.player.id === playerId ? false : pick.isVice,
    })),
  };
}

export function setVice(draft: SquadDraft, playerId: string): SquadDraft {
  const target = draft.picks.find((pick) => pick.player.id === playerId);
  if (!target || !target.isStarting) return draft;
  return {
    picks: draft.picks.map((pick) => ({
      ...pick,
      isVice: pick.player.id === playerId,
      isCaptain: pick.player.id === playerId ? false : pick.isCaptain,
    })),
  };
}

/** Map a saved squad (server picks) back into a draft. */
export function draftFromPicks(
  picks: { player: PlayerWithTeam | null; is_starting: boolean; is_captain: boolean; is_vice: boolean }[],
): SquadDraft {
  return {
    picks: picks
      .filter((pick): pick is typeof pick & { player: PlayerWithTeam } => pick.player !== null)
      .map((pick) => ({
        player: pick.player,
        isStarting: pick.is_starting,
        isCaptain: pick.is_captain,
        isVice: pick.is_vice,
      })),
  };
}

/** Serialize for the save_squad RPC: starters get slots 1..11, bench after. */
export function toRpcPicks(draft: SquadDraft): SquadPickInput[] {
  const starters = draft.picks.filter((pick) => pick.isStarting);
  const bench = draft.picks.filter((pick) => !pick.isStarting);
  const ordered = [...starters, ...bench];
  return ordered.map((pick, index) => ({
    player_id: pick.player.id,
    slot: index + 1,
    is_starting: pick.isStarting,
    is_captain: pick.isCaptain,
    is_vice: pick.isVice,
  }));
}

/** Starters grouped into pitch rows (GK at the back). */
export function pitchRows(draft: SquadDraft): Record<Position, DraftPick[]> {
  const rows: Record<Position, DraftPick[]> = { GK: [], DEF: [], MID: [], FWD: [] };
  for (const pick of draft.picks) {
    if (pick.isStarting) rows[pick.player.position].push(pick);
  }
  return rows;
}

export function benchPicks(draft: SquadDraft): DraftPick[] {
  return draft.picks.filter((pick) => !pick.isStarting);
}

export interface PitchLayout {
  /** Starting picks grouped by position. */
  starters: Record<Position, DraftPick[]>;
  /** Bench picks grouped by position. */
  bench: Record<Position, DraftPick[]>;
  /** Slots per position in the starting XI (formation max - fixed for the WC). */
  startingTarget: Record<Position, number>;
  /** Bench slots per position (squad quota minus the starting target). */
  benchTarget: Record<Position, number>;
}

/**
 * The full pitch picture: starters and bench grouped by position, plus the
 * target slot counts so the builder can always draw EVERY position row (filled
 * chips first, dashed empties after) instead of hiding empty rows. This is what
 * makes a fresh squad show a complete, consistent pitch from the first tap.
 */
export function pitchLayout(draft: SquadDraft, rules: FormationRules): PitchLayout {
  const starters: Record<Position, DraftPick[]> = { GK: [], DEF: [], MID: [], FWD: [] };
  const bench: Record<Position, DraftPick[]> = { GK: [], DEF: [], MID: [], FWD: [] };
  for (const pick of draft.picks) {
    (pick.isStarting ? starters : bench)[pick.player.position].push(pick);
  }
  const startingTarget: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  const benchTarget: Record<Position, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const position of POSITIONS) {
    startingTarget[position] = rules.starting[position][1];
    benchTarget[position] = Math.max(0, rules.squad[position] - rules.starting[position][1]);
  }
  return { starters, bench, startingTarget, benchTarget };
}
